"use client";

import { Checkbox } from "@shared/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/components/ui/popover";
import { ChevronDownIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { OrgProjectChip, type OrgProjectOption } from "@/components/org/org-project-chip";
import { cn } from "@/lib/utils";

type OrgProjectMultiSelectProps = {
  projects: OrgProjectOption[];
  value: string[];
  onValueChange: (ids: string[]) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  placeholder?: string;
};

export const OrgProjectMultiSelect = ({
  projects,
  value,
  onValueChange,
  disabled = false,
  id,
  className,
  placeholder = "Select",
}: OrgProjectMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => projects.filter((project) => value.includes(project.id)), [projects, value]);

  const toggle = (projectId: string) => {
    if (value.includes(projectId)) {
      onValueChange(value.filter((id) => id !== projectId));
      return;
    }
    onValueChange([...value, projectId]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        disabled={disabled}
        aria-label="Projects"
        className={cn(
          "border-input bg-background flex min-h-9 w-full items-center justify-between rounded-md border px-3 py-1.5 text-sm shadow-xs",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        <span className="flex flex-1 flex-wrap gap-1.5 text-left">
          {selected.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            selected.map((project) => <OrgProjectChip key={project.id} name={project.name} color={project.color} />)
          )}
        </span>
        <ChevronDownIcon className="text-muted-foreground size-4 shrink-0 opacity-60" aria-hidden />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <ul className="flex max-h-60 flex-col gap-1 overflow-y-auto">
          {projects.map((project) => {
            const checked = value.includes(project.id);
            return (
              <li key={project.id}>
                <button
                  type="button"
                  className="hover:bg-accent flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left"
                  onClick={() => toggle(project.id)}
                >
                  <Checkbox checked={checked} className="pointer-events-none" tabIndex={-1} />
                  <OrgProjectChip name={project.name} color={project.color} />
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

type OrgProjectSelectProps = {
  projects: OrgProjectOption[];
  value: string | null;
  onValueChange: (id: string) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  placeholder?: string;
};

export const OrgProjectSelect = ({
  projects,
  value,
  onValueChange,
  disabled = false,
  id,
  className,
  placeholder = "Select",
}: OrgProjectSelectProps) => {
  const [open, setOpen] = useState(false);
  const selected = projects.find((project) => project.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        disabled={disabled}
        className={cn(
          "border-input bg-background flex h-9 w-full items-center justify-between rounded-md border px-3 text-sm shadow-xs",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        {selected ? (
          <OrgProjectChip name={selected.name} color={selected.color} />
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDownIcon className="text-muted-foreground size-4 opacity-60" aria-hidden />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-1">
        <ul className="flex max-h-60 flex-col">
          {projects.map((project) => (
            <li key={project.id}>
              <button
                type="button"
                className="hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
                onClick={() => {
                  onValueChange(project.id);
                  setOpen(false);
                }}
              >
                <OrgProjectChip name={project.name} color={project.color} />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
